/**
 * Authentication Service
 * Handles user authentication for both staff and customer apps
 * Uses Firebase Authentication + Firestore for user profiles
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  User,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

export interface UserProfile {
  uid: string;
  email: string;
  role: 'staff' | 'customer';
  isOwner?: boolean; // real owners often carry role 'customer'/'staff' + isOwner:true — the authority flag
  name?: string;
  phone?: string;
  shopId?: string;   // multi-tenant: which shop this user belongs to (gates scoped reads + rules)
  createdAt: Date;
  lastLogin: Date;
}

class AuthService {
  /**
   * Validate password meets security requirements
   * Returns null if valid, error message if invalid
   */
  validatePassword(password: string): string | null {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  }

  /**
   * Validate phone number is in E.164 format (+1XXXXXXXXXX)
   * Returns null if valid, error message if invalid
   */
  validatePhoneNumber(phoneNumber: string): string | null {
    const e164Regex = /^\+[1-9]\d{6,14}$/;
    if (!e164Regex.test(phoneNumber)) {
      return 'Phone number must be in international format (e.g. +1234567890)';
    }
    return null;
  }

  /**
   * Sign up new user with email/password
   */
  async signUp(
    email: string,
    password: string,
    role: 'staff' | 'customer',
    name?: string
  ): Promise<UserProfile> {
    try {
      // Validate password strength before sending to Firebase
      const passwordError = this.validatePassword(password);
      if (passwordError) {
        throw new Error(passwordError);
      }

      const credential = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name
      if (name) {
        await updateProfile(credential.user, { displayName: name });
      }

      const userProfile: UserProfile = {
        uid: credential.user.uid,
        email,
        role,
        name,
        createdAt: new Date(),
        lastLogin: new Date()
      };

      // Save to /users collection
      await setDoc(doc(db, 'users', credential.user.uid), {
        uid: credential.user.uid,
        email,
        role,
        name,
        createdAt: Timestamp.now(),
        lastLogin: Timestamp.now()
      });

      return userProfile;
    } catch (error: any) {
      if (error.message && !error.code) throw error; // Re-throw validation errors as-is
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Sign in with email/password
   */
  async signIn(email: string, password: string): Promise<UserProfile> {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;

      // LOGIN MUST NOT BLOCK ON FIRESTORE (Jose 2026-09-24: "sign in taking too long"). Auth already
      // succeeded above — the post-auth Firestore calls are NOT required to get in:
      //  • lastLogin = bookkeeping → fire-and-forget, never awaited (a wedged write would hang login).
      //  • profile   = re-loaded resiliently by onAuthStateChanged right after navigation, so cap the
      //    read at 5s and fall back to a minimal profile rather than hanging the whole sign-in.
      updateDoc(doc(db, 'users', uid), { lastLogin: Timestamp.now() }).catch(() => {});

      try {
        return await Promise.race([
          this.getUserProfile(uid),
          new Promise<UserProfile>((_, reject) => setTimeout(() => reject(new Error('profile-timeout')), 5000)),
        ]);
      } catch {
        // Return-type contract only; AuthContext.login ignores this and onAuthStateChanged resolves
        // the real role. Never block the login on a slow/wedged profile read.
        return {
          uid,
          email: credential.user.email || email,
          role: 'staff',
          createdAt: new Date(),
          lastLogin: new Date(),
        } as UserProfile;
      }
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Sign in with phone number (for customers)
   * Step 1: Send verification code
   */
  async signInWithPhone(
    phoneNumber: string,
    recaptchaContainer: HTMLElement
  ): Promise<ConfirmationResult> {
    // Validate phone number format before calling Firebase
    const phoneError = this.validatePhoneNumber(phoneNumber);
    if (phoneError) {
      throw new Error(phoneError);
    }

    try {
      const appVerifier = new RecaptchaVerifier(auth, recaptchaContainer, {
        size: 'invisible',
        callback: () => {}
      });

      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      return confirmationResult;
    } catch (error: any) {
      console.error('Phone sign in error:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Verify phone code and complete sign-in
   * Step 2: Confirm with code
   */
  async verifyPhoneCode(
    confirmationResult: ConfirmationResult,
    code: string,
    name?: string
  ): Promise<UserProfile> {
    try {
      const result = await confirmationResult.confirm(code);

      // Check if user profile exists
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));

      if (!userDoc.exists()) {
        // Create new customer profile
        const userProfile: UserProfile = {
          uid: result.user.uid,
          email: result.user.email || '',
          role: 'customer',
          name,
          phone: result.user.phoneNumber || undefined,
          createdAt: new Date(),
          lastLogin: new Date()
        };

        await setDoc(doc(db, 'users', result.user.uid), {
          uid: result.user.uid,
          email: result.user.email || '',
          role: 'customer',
          name,
          phone: result.user.phoneNumber,
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now()
        });

        // New customer profile created
        return userProfile;
      }

      // Update last login
      await updateDoc(doc(db, 'users', result.user.uid), {
        lastLogin: Timestamp.now()
      });

      return await this.getUserProfile(result.user.uid);
    } catch (error: any) {
      throw new Error('Invalid verification code');
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Get user profile from Firestore
   */
  async getUserProfile(uid: string): Promise<UserProfile> {
    try {
      const docSnap = await getDoc(doc(db, 'users', uid));

      if (!docSnap.exists()) {
        throw new Error('User profile not found');
      }

      const data = docSnap.data();
      return {
        uid: data.uid,
        email: data.email,
        role: data.role,
        isOwner: data.isOwner === true, // carry the owner authority flag through to the app (was dropped)
        name: data.name,
        phone: data.phone,
        shopId: data.shopId,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate() || new Date()
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', uid), {
        ...updates,
        lastUpdated: Timestamp.now()
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if current user is staff
   */
  async isStaff(): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) return false;

    try {
      const profile = await this.getUserProfile(user.uid);
      return profile.role === 'staff';
    } catch {
      return false;
    }
  }

  /**
   * Check if current user is customer
   */
  async isCustomer(): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) return false;

    try {
      const profile = await this.getUserProfile(user.uid);
      return profile.role === 'customer';
    } catch {
      return false;
    }
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return auth.onAuthStateChanged(callback);
  }

  /**
   * Convert Firebase error codes to user-friendly messages
   */
  private getErrorMessage(errorCode: string): string {
    const errorMessages: Record<string, string> = {
      'auth/email-already-in-use': 'This email is already registered',
      'auth/invalid-email': 'Invalid email address',
      'auth/operation-not-allowed': 'Operation not allowed',
      'auth/weak-password': 'Password must be at least 8 characters with uppercase, lowercase, and a number',
      'auth/user-disabled': 'This account has been disabled',
      'auth/user-not-found': 'No account found with this email',
      'auth/wrong-password': 'Incorrect password',
      'auth/invalid-credential': 'Invalid login credentials',
      'auth/too-many-requests': 'Too many attempts. Please try again later',
      'auth/network-request-failed': 'Network error. Check your connection',
      'auth/invalid-phone-number': 'Invalid phone number',
      'auth/missing-phone-number': 'Phone number is required',
      'auth/quota-exceeded': 'SMS quota exceeded',
      'auth/invalid-verification-code': 'Invalid verification code',
      'auth/invalid-verification-id': 'Verification session expired'
    };

    return errorMessages[errorCode] || 'An error occurred. Please try again';
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export types
export type { User };
